"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import { cn } from "@/lib/utils";
import Script from "next/script";

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface BreadcrumbProps {
  className?: string;
  items?: BreadcrumbItem[];
}

export const Breadcrumb = ({ className, items }: BreadcrumbProps) => {
  const pathname = usePathname();
  const domain = "https://edify.club"; // Update this with your actual domain

  // If items are not provided, generate from current path
  const pathSegments = pathname.split("/").filter(Boolean);
  const generatedItems: BreadcrumbItem[] = pathSegments.map((segment, index) => {
    const href = "/" + pathSegments.slice(0, index + 1).join("/");
    return { label: decodeURIComponent(segment), href };
  });

  const breadcrumbs = items ?? [{ label: "Home", href: "/" }, ...generatedItems];

  // Generate BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.label.replaceAll("-", " "),
      "item": `${domain}${crumb.href}`
    }))
  };

  return (
    <nav className={cn("text-sm", className)}>
      <Script
        id="breadcrumb-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <ul className="flex flex-wrap items-center gap-1 text-gray-600">
        {breadcrumbs.map((crumb, index) => (
          <li key={crumb.href} className="flex items-center gap-1">
            <Link
              href={crumb.href}
              className={cn(
                "capitalize transition duration-150",
                index === breadcrumbs.length - 1
                  ? "text-gray-600 pointer-events-none"
                  : "text-gray-600 hover:underline"
              )}
            >
              {crumb.label.length<=24 ? crumb.label.replaceAll("-", " ") : `${crumb.label.replaceAll("-", " ").substring(0,24)}...`}
            </Link>
            {index < breadcrumbs.length - 1 && (
              <span className="mx-1 text-gray-400">/</span>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};
